<template>
  <v-container class="py-4">
    <v-card class="pa-4 mb-4">
      <h2 class="mb-1">Koneko B (Task Synthesis Starter)</h2>
      <div class="caption grey--text text--darken-1">
        Canonical merged task feed from GitLab + Asana.
      </div>
      <div class="mt-3 d-flex align-center flex-wrap" style="gap: 8px">
        <v-btn small color="primary" :loading="loading" @click="refreshNow"
          >Refresh</v-btn
        >
        <span class="caption grey--text text--darken-1">
          Saves your tokens locally, resolves Asana workspace / projects when
          possible, then loads tasks.
        </span>
        <v-chip small outlined>Total tasks: {{ tasks.length }}</v-chip>
        <v-chip small outlined>Assignees: {{ assigneeSummary.length }}</v-chip>
        <v-chip small outlined>GitLab: {{ sourceCounts.gitlab }}</v-chip>
        <v-chip small outlined>Asana: {{ sourceCounts.asana }}</v-chip>
      </div>
    </v-card>

    <v-alert v-if="loadError" type="error" outlined>{{ loadError }}</v-alert>
    <v-alert v-if="sourceNotice" type="info" outlined>{{
      sourceNotice
    }}</v-alert>

    <v-card class="pa-4 mb-4">
      <h3 class="mb-1">Connections</h3>
      <div class="caption grey--text text--darken-1 mb-3">
        Easiest workflow: duplicate <strong>.env.example</strong> to
        <strong>.env</strong> (gitignored) beside package.json, add
        <strong>VUE_APP_KONEKO_B_*</strong> tokens, restart the dev server.
        Leave these fields blank if you prefer. Anything saved in this browser
        overrides .env when the field is non-empty — use
        <strong>Clear saved tokens</strong> below if Asana shows 401 but your
        PAT in .env is correct. Asana workspace / project mapping stays
        automatic when possible; custom-field pinning lives under Advanced.
      </div>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="asanaConfig.gitlabToken"
            label="GitLab PAT"
            outlined
            dense
            hide-details
            type="password"
            autocomplete="off"
            placeholder="glpat-…"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="asanaConfig.token"
            label="Asana PAT"
            outlined
            dense
            hide-details
            type="password"
            autocomplete="off"
            placeholder="personal access token"
          />
        </v-col>
      </v-row>
      <div class="mt-2 mb-2">
        <v-btn
          small
          outlined
          color="grey darken-1"
          @click="clearSavedTokensUseEnv"
        >
          Clear saved tokens (use .env only)
        </v-btn>
        <span class="caption grey--text text--darken-1 ml-3"
          >Wipes localStorage for Koneko B; reloads form from .env.</span
        >
      </div>
      <v-row v-if="asanaWorkspaceChoices.length > 1" align="center">
        <v-col cols="12" md="8">
          <v-select
            v-model="asanaConfig.workspaceGid"
            :items="asanaWorkspaceChoices"
            label="Asana workspace"
            outlined
            dense
            hide-details
            clearable
            item-text="name"
            item-value="gid"
            placeholder="Choose workspace"
            @change="persistAsanaAndHint"
          />
        </v-col>
        <v-col cols="12" md="4" class="caption grey--text">
          Needed when your account sees more than one workspace.
        </v-col>
      </v-row>

      <v-expansion-panels v-model="advancedSetupPanels" flat class="mt-2">
        <v-expansion-panel>
          <v-expansion-panel-header class="px-0">
            Advanced (URLs, overrides, diagnostics)
          </v-expansion-panel-header>
          <v-expansion-panel-content class="px-0 pt-2">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="asanaConfig.gitlabBaseUrl"
                  label="GitLab base URL"
                  outlined
                  dense
                  hide-details
                  placeholder="https://nekogit.nekoapi.xyz"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="asanaConfig.workspaceGid"
                  label="Workspace GID (manual override)"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="asanaConfig.projectGids"
                  label="Project GIDs override (comma-separated)"
                  outlined
                  dense
                  hide-details
                  placeholder="leave empty for auto-map"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="asanaConfig.assigneeGid"
                  label="Asana assignee filter"
                  outlined
                  dense
                  hide-details
                  placeholder="me"
                />
              </v-col>
            </v-row>
            <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
              <v-btn
                small
                outlined
                color="indigo"
                :loading="hydratingWorkspaces"
                @click="reloadWorkspaceChoices"
              >
                Reload workspaces
              </v-btn>
              <v-btn
                small
                outlined
                color="deep-purple"
                :loading="loadingAsanaProjects"
                @click="forceRemapAsanaProjectsFromApi"
              >
                Clear mapped projects & re-scan
              </v-btn>
            </div>
            <div class="caption grey--text text--darken-1 mb-3">
              Custom-field mapping (only if auto-detect guesses wrong): we match
              number fields whose names resemble &quot;estimate&quot; /
              &quot;spent&quot; unless you pin them below.
            </div>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="asanaConfig.asanaEstimateFieldName"
                  label="Estimate field name"
                  outlined
                  dense
                  hide-details
                  placeholder="e.g. Estimated hours"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="asanaConfig.asanaEstimateFieldGid"
                  label="Estimate field GID"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="asanaConfig.asanaEstimateUnit"
                  :items="['hours', 'minutes']"
                  label="Estimate unit"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="asanaConfig.asanaSpentFieldName"
                  label="Spent field name"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="asanaConfig.asanaSpentFieldGid"
                  label="Spent field GID"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="asanaConfig.asanaSpentUnit"
                  :items="['hours', 'minutes']"
                  label="Spent unit"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
            </v-row>
            <v-btn
              small
              color="secondary"
              class="mr-2"
              @click="persistAsanaQuiet"
              >Apply advanced settings</v-btn
            >

            <v-alert
              v-if="asanaProjectLoadError"
              dense
              class="mt-3"
              type="warning"
              outlined
              >{{ asanaProjectLoadError }}</v-alert
            >
            <v-simple-table v-if="asanaProjects.length" dense class="mt-3">
              <thead>
                <tr>
                  <th>Asana project</th>
                  <th>Canonical</th>
                  <th>GID</th>
                  <th>Mapped?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in asanaProjects" :key="p.gid">
                  <td>{{ p.name }}</td>
                  <td>{{ p.canonicalName }}</td>
                  <td>{{ p.gid }}</td>
                  <td>{{ isAutoMappedProject(p) ? "yes" : "no" }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <v-card class="pa-4 mb-4">
      <h3 class="mb-1">Task queue</h3>
      <div class="caption grey--text text--darken-1 mb-3">
        Score follows management-bot weights (priority labels, client heuristic,
        estimated / assigned penalties, default project priority
        {{ defaultProjectPriority }}). Fire is 0–3 from milestone due vs the
        bot’s “two Fridays” anchor (task due date if no milestone). Sort by
        score or fire below. GitLab and Asana assignees are merged when they map
        to the same person (e.g. username vs full name slug); add aliases in
        code or localStorage if you see duplicates.
        <strong> NekoHR estimate (h)</strong> is stored in this browser only and
        overrides API values for scoring; leave blank and blur to fall back to
        GitLab/Asana.
      </div>

      <v-row align="center" class="mb-2">
        <v-col cols="12" md="4">
          <v-select
            v-model="queueAssigneeFilter"
            :items="queueAssigneeItems"
            label="Assignee"
            outlined
            dense
            hide-details
            item-text="text"
            item-value="value"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="queueSortBy"
            :items="queueSortItems"
            label="Sort"
            outlined
            dense
            hide-details
            item-text="text"
            item-value="value"
          />
        </v-col>
        <v-col cols="12" md="4" class="d-flex flex-wrap" style="gap: 8px">
          <span class="caption grey--text align-self-center mr-1"
            >Filter tools:</span
          >
          <v-chip
            v-for="def in toolChipCatalog"
            :key="def.id"
            small
            :color="queueToolFilters.includes(def.id) ? def.color : undefined"
            :outlined="!queueToolFilters.includes(def.id)"
            :dark="queueToolFilters.includes(def.id)"
            clickable
            @click="toggleQueueTool(def.id)"
          >
            {{ def.label }}
            <span v-if="activeQueueToolSummary[def.id]" class="ml-1 caption"
              >({{ activeQueueToolSummary[def.id] }})</span
            >
          </v-chip>
        </v-col>
      </v-row>

      <div
        v-if="queueAssigneeFilter === '__me__' && !profileAssigneeKey"
        class="caption amber--text text--darken-2 mb-3"
      >
        No logged-in username for “Me”; use the assignee dropdown to pick
        yourself.
      </div>

      <div
        v-if="presentedByProject.length === 0"
        class="caption grey--text text--darken-1 mb-3"
      >
        No tasks in this queue. Try “All assignees”, pick another person, or
        clear tool filters.
      </div>

      <div v-for="group in presentedByProject" :key="group.name" class="mb-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <h4 class="subtitle-1 font-weight-medium mb-0">{{ group.name }}</h4>
          <v-chip small outlined>{{ group.tasks.length }} tasks</v-chip>
        </div>
        <v-card
          v-for="task in group.tasks"
          :key="task.sourceTaskId"
          outlined
          class="mb-3 pa-3 koneko-b-task-shell"
          :class="urgencyBorderClass(task.urgencyLevel)"
        >
          <div class="d-flex flex-column flex-md-row justify-space-between">
            <div class="flex-grow-1 pr-md-4" style="min-width: 0">
              <a
                class="subtitle-1 font-weight-medium d-block text-truncate mb-2"
                :href="task.externalUrl || '#'"
                target="_blank"
                rel="noreferrer"
                >{{ task.title }}</a
              >
              <div class="d-flex flex-wrap mb-2" style="gap: 6px">
                <v-chip
                  x-small
                  dark
                  :color="task.source === 'gitlab' ? 'deep-purple' : 'teal'"
                >
                  {{ task.source }}
                </v-chip>
                <v-chip
                  v-for="tid in task.tools"
                  :key="tid"
                  x-small
                  :color="chipForTool(tid).color"
                  dark
                  class="font-weight-medium"
                >
                  {{ chipForTool(tid).label }}
                </v-chip>
              </div>
              <div
                v-if="(task.description || '').trim()"
                class="caption koneko-b-desc-preview text--secondary mt-2"
              >
                {{ task.description }}
              </div>
              <div class="caption grey--text text--darken-1">
                <span v-if="task.nekohrEstimateOverride"
                  >NekoHR est. {{ minutesToHours(task.estimateMinutes) }}h</span
                >
                <span v-else
                  >Source est. {{ minutesToHours(task.estimateMinutes) }}h</span
                >
                <span
                  v-if="
                    task.nekohrEstimateOverride &&
                    differsFromSourceEstimate(task)
                  "
                >
                  (was {{ minutesToHours(task.sourceEstimateMinutes) }}h from
                  API)
                </span>
                · Spent {{ minutesToHours(task.spentMinutes) }}h ·
                {{ dueSnippet(task.dueDate) }} · Updated
                {{ (task.updatedAt || "").slice(0, 10) }}
              </div>
              <div class="d-flex flex-wrap align-center mt-2" style="gap: 8px">
                <v-text-field
                  class="koneko-b-est-field"
                  style="max-width: 160px"
                  dense
                  hide-details
                  outlined
                  label="NekoHR est. (h)"
                  :placeholder="sourceEstPlaceholder(task)"
                  :value="estimateFieldString(task)"
                  @blur="(e) => commitEstimateFromQueue(task, e.target.value)"
                  @keyup.enter="
                    (e) => commitEstimateFromQueue(task, e.target.value)
                  "
                />
                <v-btn
                  v-if="task.nekohrEstimateOverride"
                  x-small
                  text
                  color="primary"
                  type="button"
                  @click="revertEstimateOverride(task)"
                >
                  Use API estimate
                </v-btn>
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  type="button"
                  class="ml-1"
                  :loading="pushBusy[task.sourceTaskId] === 'est'"
                  :disabled="
                    !Number.isFinite(task.estimateMinutes) ||
                    task.estimateMinutes <= 0
                  "
                  @click="pushEstimateToService(task)"
                >
                  Push estimate to {{ task.source }}
                </v-btn>
                <v-checkbox
                  v-if="task.source === 'asana'"
                  v-model="asanaMirrorEstInTitle"
                  class="mt-0 pt-0 ml-2"
                  dense
                  hide-details
                  label="Also [Est: …] in title when no CF"
                />
              </div>
              <div
                v-if="metadataDraft[task.sourceTaskId]"
                class="d-flex flex-wrap align-end mt-3"
                style="gap: 10px"
              >
                <v-text-field
                  v-model="metadataDraft[task.sourceTaskId].dueDate"
                  type="date"
                  dense
                  hide-details
                  outlined
                  label="Due date"
                  style="max-width: 168px"
                  class="koneko-b-meta-field"
                />
                <v-select
                  v-model="metadataDraft[task.sourceTaskId].priorityLabel"
                  :items="prioritySelectItems"
                  dense
                  hide-details
                  outlined
                  label="Priority label"
                  style="max-width: 220px"
                  class="koneko-b-meta-field"
                />
                <v-btn
                  small
                  color="primary"
                  :loading="pushBusy[task.sourceTaskId] === 'meta'"
                  @click="pushDueAndPriority(task)"
                >
                  Save due / priority
                </v-btn>
                <span
                  class="caption grey--text text--darken-1 align-self-center"
                >
                  Writes to GitLab issue labels + due date, or Asana due_on +
                  workspace tags named like GitLab.
                </span>
              </div>
            </div>
            <div
              class="d-flex flex-column align-center align-md-end justify-end mt-2 mt-md-0"
              style="gap: 8px; flex-shrink: 0"
            >
              <v-chip
                small
                color="success"
                dark
                outlined
                class="font-weight-bold"
              >
                Score: {{ formatScore(task.managementScore) }}
              </v-chip>
              <v-chip
                small
                color="deep-orange"
                dark
                outlined
                class="font-weight-bold"
              >
                Fire {{ task.managementFire }}/3
              </v-chip>
              <div class="subtitle-2" aria-label="fire">
                {{ fireEmoji(task.managementFire) }}
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </v-card>

    <v-card class="pa-4 mb-4">
      <h3 class="mb-3">Assignee Load</h3>
      <v-simple-table dense>
        <thead>
          <tr>
            <th>Assignee</th>
            <th>Tasks</th>
            <th>Est. (h)</th>
            <th>Spent (h)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in assigneeSummary" :key="row.assigneeKey">
            <td>{{ row.assigneeLabel }}</td>
            <td>{{ row.count }}</td>
            <td>{{ minutesToHours(row.estimateMinutes) }}</td>
            <td>{{ minutesToHours(row.spentMinutes) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <v-card class="pa-4">
      <h3 class="mb-1">Raw canonical feed</h3>
      <div class="caption grey--text text--darken-1 mb-3">
        Full merged list (most recent first). Use the queue above for scored /
        tool-aware working order.
      </div>
      <v-expansion-panels v-model="rawFeedPanel" class="mb-3">
        <v-expansion-panel>
          <v-expansion-panel-header>Table view</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Task</th>
                  <th>Assignee</th>
                  <th>Status</th>
                  <th>Est. (source)</th>
                  <th>NekoHR (h)</th>
                  <th>Spent</th>
                  <th>Project</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="task in tasks" :key="task.sourceTaskId">
                  <td>
                    <v-chip
                      x-small
                      :color="task.source === 'gitlab' ? 'deep-purple' : 'teal'"
                      dark
                    >
                      {{ task.source }}
                    </v-chip>
                  </td>
                  <td>
                    <a
                      :href="task.externalUrl"
                      target="_blank"
                      rel="noreferrer"
                      >{{ task.title }}</a
                    >
                  </td>
                  <td>{{ task.assigneeLabel }}</td>
                  <td>{{ task.status }}</td>
                  <td>{{ minutesToHours(task.sourceEstimateMinutes) }}</td>
                  <td>
                    <v-text-field
                      class="koneko-b-est-field"
                      dense
                      hide-details
                      single-line
                      style="max-width: 88px"
                      :value="estimateFieldString(task)"
                      @blur="
                        (e) => commitEstimateFromQueue(task, e.target.value)
                      "
                      @keyup.enter="
                        (e) => commitEstimateFromQueue(task, e.target.value)
                      "
                    />
                  </td>
                  <td>{{ minutesToHours(task.spentMinutes) }}</td>
                  <td>{{ task.projectLabel }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </v-container>
</template>

<style scoped>
.koneko-b-urge {
  border-left-width: 4px;
  border-left-style: solid;
}
.koneko-b-urge-low {
  border-left-color: rgba(128, 128, 128, 0.35);
}
.koneko-b-urge-mid {
  border-left-color: #ffb74d;
}
.koneko-b-urge-high {
  border-left-color: #f4511e;
}
.koneko-b-task-shell {
  border-radius: 10px;
}
.koneko-b-desc-preview {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
}
.koneko-b-est-field >>> .v-input__slot {
  min-height: 34px !important;
}
.koneko-b-meta-field >>> .v-input__slot {
  min-height: 36px !important;
}
</style>

<script>
import {mapGetters} from "vuex";
import {
  fetchGitLabTasks,
  fetchAsanaTasks,
  fetchAsanaProjects,
  ensureAsanaConnectionPrimed,
  fetchAsanaWorkspaceList,
  summarizeByAssignee,
  readAsanaConfig,
  saveAsanaConfig,
  clearSavedKonekoBConfig,
  isKnownAsanaProjectName,
  enrichTaskPresentation,
  sortTasksPresentationOrder,
  summarizeTools,
  pushTaskDueAndPriorityToService,
  pushAsanaEstimateToService,
  pushGitLabEstimateToService,
} from "@/services/konekoB/canonicalTaskFeed";
import {
  DEFAULT_PROJECT_PRIORITY_WEIGHT,
  GITLAB_STYLE_PRIORITY_LABELS,
  gitlabPriorityLabel,
} from "@/services/konekoB/managementBotScore";
import {normalizeTaskAssignee} from "@/services/konekoB/konekoBAssigneeIdentity";
import {
  loadEstimateOverlays,
  saveEstimateOverlays,
  mergeTaskWithEstimateOverlay,
  setEstimateOverlayHours,
} from "@/services/konekoB/konekoBEstimateOverlay";

export default {
  name: "KonekoBView",
  data() {
    return {
      loading: false,
      loadError: "",
      tasksBase: [],
      estimateOverlays: {},
      sourceNotice: "",
      sourceErrors: {
        gitlab: "",
        asana: "",
      },
      loadingAsanaProjects: false,
      hydratingWorkspaces: false,
      asanaProjectLoadError: "",
      asanaProjects: [],
      asanaWorkspaceChoices: [],
      advancedSetupPanels: undefined,
      queueAssigneeFilter: "__all__",
      queueSortBy: "score",
      queueToolFilters: [],
      rawFeedPanel: null,
      metadataDraft: {},
      pushBusy: {},
      asanaMirrorEstInTitle: false,
      asanaConfig: {
        gitlabToken: "",
        gitlabBaseUrl: "https://nekogit.nekoapi.xyz",
        token: "",
        projectGids: "",
        workspaceGid: "",
        assigneeGid: "me",
        asanaEstimateFieldGid: "",
        asanaEstimateFieldName: "",
        asanaEstimateUnit: "hours",
        asanaSpentFieldGid: "",
        asanaSpentFieldName: "",
        asanaSpentUnit: "hours",
      },
      toolChipCatalog: [
        {id: "NEEDS_DUE_DATE", label: "Needs due date", color: "cyan darken-1"},
        {
          id: "NEEDS_PRIORITY",
          label: "Needs priority",
          color: "purple darken-1",
        },
        {
          id: "NEEDS_SUBTASKING",
          label: "Needs subtasking",
          color: "deep-orange darken-1",
        },
        {
          id: "NEEDS_ESTIMATE",
          label: "Needs estimate",
          color: "amber darken-2",
        },
        {id: "UNASSIGNED", label: "Unassigned", color: "brown"},
        {id: "NEEDS_DISCUSSION", label: "Discussion", color: "red darken-1"},
        {id: "NEEDS_MORE_INFO", label: "More info", color: "blue-grey"},
        {id: "BLOCKED", label: "Blocked", color: "grey darken-2"},
      ],
      queueSortItems: [
        {text: "Score (management-bot)", value: "score"},
        {text: "Fire (0–3)", value: "fire"},
        {text: "Recently updated", value: "updated"},
      ],
    };
  },
  computed: {
    ...mapGetters(["userProfileData"]),
    tasks() {
      return this.tasksBase.map((t) =>
        mergeTaskWithEstimateOverlay(t, this.estimateOverlays)
      );
    },
    assigneeSummary() {
      return summarizeByAssignee(this.tasks);
    },
    sourceCounts() {
      const out = {gitlab: 0, asana: 0};
      this.tasks.forEach((t) => {
        if (t.source === "gitlab") out.gitlab += 1;
        if (t.source === "asana") out.asana += 1;
      });
      return out;
    },
    profileAssigneeKey() {
      return String(this.userProfileData?.username || "")
        .trim()
        .toLowerCase();
    },
    defaultProjectPriority() {
      return DEFAULT_PROJECT_PRIORITY_WEIGHT;
    },
    queueAssigneeItems() {
      const items = [
        {text: "All assignees", value: "__all__"},
        {text: "Me (logged-in)", value: "__me__"},
      ];
      const seen = new Set(["__all__", "__me__"]);
      [...this.assigneeSummary]
        .sort((a, b) =>
          String(a.assigneeLabel || "").localeCompare(
            String(b.assigneeLabel || "")
          )
        )
        .forEach((row) => {
          const v = row.assigneeKey || "unknown";
          if (!seen.has(v)) {
            seen.add(v);
            items.push({text: row.assigneeLabel || v, value: v});
          }
        });
      return items;
    },
    presentedTasksFiltered() {
      const me = this.profileAssigneeKey;
      let rows = this.tasks.map((t) => enrichTaskPresentation(t));
      const f = this.queueAssigneeFilter;
      if (f === "__me__" && me) {
        rows = rows.filter((t) => t.assigneeKey === me);
      } else if (f !== "__all__" && f && f !== "__me__") {
        rows = rows.filter((t) => t.assigneeKey === f);
      }
      if (this.queueToolFilters.length > 0) {
        rows = rows.filter((t) =>
          this.queueToolFilters.some((id) => (t.tools || []).includes(id))
        );
      }
      return sortTasksPresentationOrder(rows, this.queueSortBy);
    },
    presentedByProject() {
      /** @type {Record<string, typeof this.presentedTasksFiltered>} */
      const groups = {};
      this.presentedTasksFiltered.forEach((t) => {
        const key = t.projectLabel || "Project";
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
      });
      return Object.keys(groups)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({name, tasks: groups[name]}));
    },
    activeQueueToolSummary() {
      return summarizeTools(this.presentedTasksFiltered);
    },
    prioritySelectItems() {
      return [
        {text: "(no priority label)", value: ""},
        ...GITLAB_STYLE_PRIORITY_LABELS.map((p) => ({text: p, value: p})),
      ];
    },
  },
  watch: {
    tasksBase: {
      handler(rows) {
        const next = {};
        (rows || []).forEach((t) => {
          const id = t.sourceTaskId;
          next[id] = {
            dueDate: t.dueDate ? String(t.dueDate).slice(0, 10) : "",
            priorityLabel: this.priorityDraftFromLabels(t),
          };
        });
        this.metadataDraft = next;
      },
      immediate: true,
    },
    advancedSetupPanels(val) {
      const expanded =
        val === 0 ||
        val === "0" ||
        (Array.isArray(val) && val.includes(0)) ||
        (Array.isArray(val) && val.includes("0"));
      if (expanded) {
        this.$nextTick(() => this.loadAsanaProjectDirectory());
      }
    },
  },
  async mounted() {
    this.estimateOverlays = loadEstimateOverlays();
    this.asanaConfig = readAsanaConfig();
    await this.refreshNow();
  },
  methods: {
    estimateFieldString(task) {
      if (!task.nekohrEstimateOverride) return "";
      return this.formatEditableHours(task.estimateMinutes);
    },
    formatEditableHours(minutes) {
      if (!Number.isFinite(minutes)) return "";
      const h = minutes / 60;
      const r = Math.round(h * 100) / 100;
      return String(r);
    },
    sourceEstPlaceholder(task) {
      const s = task.sourceEstimateMinutes;
      if (!Number.isFinite(s)) return "No API estimate";
      return `API ${this.minutesToHours(s)}h`;
    },
    differsFromSourceEstimate(task) {
      const a = task.estimateMinutes;
      const b = task.sourceEstimateMinutes;
      const finiteA = Number.isFinite(a);
      const finiteB = Number.isFinite(b);
      if (finiteA !== finiteB) return true;
      if (!finiteA) return false;
      return Math.abs(a - b) > 0.5;
    },
    commitEstimateFromQueue(task, raw) {
      const s = String(raw ?? "")
        .trim()
        .replace(",", ".");
      if (s === "") {
        const next = setEstimateOverlayHours(
          this.estimateOverlays,
          task.sourceTaskId,
          null
        );
        this.estimateOverlays = next;
        saveEstimateOverlays(next);
        return;
      }
      const h = parseFloat(s);
      if (!Number.isFinite(h) || h < 0) return;
      const next = setEstimateOverlayHours(
        this.estimateOverlays,
        task.sourceTaskId,
        h
      );
      this.estimateOverlays = next;
      saveEstimateOverlays(next);
    },
    revertEstimateOverride(task) {
      const next = setEstimateOverlayHours(
        this.estimateOverlays,
        task.sourceTaskId,
        null
      );
      this.estimateOverlays = next;
      saveEstimateOverlays(next);
    },
    priorityDraftFromLabels(task) {
      const p = gitlabPriorityLabel(task.labels || []);
      if (p === "Priority: Unprioritized") return "";
      if (GITLAB_STYLE_PRIORITY_LABELS.includes(p)) return p;
      return "";
    },
    async pushEstimateToService(task) {
      const id = task.sourceTaskId;
      this.$set(this.pushBusy, id, "est");
      this.loadError = "";
      try {
        if (task.source === "gitlab") {
          await pushGitLabEstimateToService(task, task.estimateMinutes);
        } else if (task.source === "asana") {
          await pushAsanaEstimateToService(
            task,
            task.estimateMinutes,
            readAsanaConfig(),
            {
              mirrorEstInTitle: this.asanaMirrorEstInTitle,
            }
          );
        } else {
          return;
        }
        await this.refreshNow();
      } catch (err) {
        this.loadError = (err && err.message) || "Could not push estimate";
      } finally {
        this.$delete(this.pushBusy, id);
      }
    },
    async pushDueAndPriority(task) {
      const id = task.sourceTaskId;
      const draft = this.metadataDraft[id];
      if (!draft) return;
      this.$set(this.pushBusy, id, "meta");
      this.loadError = "";
      try {
        await pushTaskDueAndPriorityToService(readAsanaConfig(), task, {
          dueDate: draft.dueDate,
          priorityLabel: draft.priorityLabel,
        });
        await this.refreshNow();
      } catch (err) {
        this.loadError =
          (err && err.message) || "Could not save due date / priority";
      } finally {
        this.$delete(this.pushBusy, id);
      }
    },
    formatScore(v) {
      const x = Number(v);
      if (!Number.isFinite(x)) return "—";
      return Number.isInteger(x) ? String(x) : x.toFixed(1);
    },
    fireEmoji(level) {
      const n = Math.max(0, Math.min(3, Number(level) || 0));
      return "🔥".repeat(n);
    },
    dueSnippet(dueDate) {
      if (!dueDate) return "No due date";
      const ms = Date.parse(String(dueDate));
      if (Number.isNaN(ms)) return `Due ${dueDate}`;
      const endUtc = ms + 86400000 - 1;
      const days = Math.ceil((endUtc - Date.now()) / 86400000);
      if (days < 0) return `${Math.abs(days)}d overdue`;
      if (days === 0) return "Due today";
      return `${days}d left`;
    },
    toggleQueueTool(id) {
      const i = this.queueToolFilters.indexOf(id);
      if (i === -1) this.queueToolFilters = [...this.queueToolFilters, id];
      else
        this.queueToolFilters = this.queueToolFilters.filter((_, j) => j !== i);
    },
    urgencyBorderClass(level) {
      const n = Math.max(0, Math.min(3, Number(level) || 0));
      if (n >= 2) return "koneko-b-urge koneko-b-urge-high";
      if (n >= 1) return "koneko-b-urge koneko-b-urge-mid";
      return "koneko-b-urge koneko-b-urge-low";
    },
    chipForTool(toolId) {
      return (
        this.toolChipCatalog.find((c) => c.id === toolId) || {
          id: toolId,
          label: toolId.replace(/_/g, " "),
          color: "grey",
        }
      );
    },
    minutesToHours(mins) {
      if (!Number.isFinite(mins)) return "—";
      return (mins / 60).toFixed(1);
    },
    clearSavedTokensUseEnv() {
      clearSavedKonekoBConfig();
      this.asanaConfig = readAsanaConfig();
      this.sourceNotice =
        "Cleared saved connection data. Tokens now come from .env (restart dev server after editing .env). Click Refresh.";
    },
    persistAsanaQuiet() {
      this.asanaConfig = saveAsanaConfig(this.asanaConfig);
    },
    persistAsanaAndHint() {
      this.persistAsanaQuiet();
      if (!this.loading) {
        this.sourceNotice =
          "Workspace saved. Click Refresh if tasks did not update.";
      }
    },
    isAutoMappedProject(project) {
      return isKnownAsanaProjectName(project.canonicalName);
    },
    /** @param {boolean} [afterSave=true] persist form to localStorage before listing */
    async reloadWorkspaceChoices(afterSave = true) {
      this.hydratingWorkspaces = true;
      this.asanaProjectLoadError = "";
      if (afterSave) {
        this.persistAsanaQuiet();
      }
      try {
        const tok = String(this.asanaConfig.token || "").trim();
        if (!tok) {
          this.asanaWorkspaceChoices = [];
          return;
        }
        const ws = await fetchAsanaWorkspaceList(tok);
        this.asanaWorkspaceChoices = ws;
        if (
          !String(this.asanaConfig.workspaceGid || "").trim() &&
          ws.length === 1
        ) {
          this.asanaConfig.workspaceGid = ws[0].gid;
          this.persistAsanaQuiet();
        }
      } catch (err) {
        this.asanaProjectLoadError =
          (err && err.message) || "Could not list Asana workspaces";
      } finally {
        this.hydratingWorkspaces = false;
      }
    },
    async loadAsanaProjectDirectory() {
      this.asanaProjectLoadError = "";
      if (
        !String(this.asanaConfig.token || "").trim() ||
        !String(this.asanaConfig.workspaceGid || "").trim()
      ) {
        this.asanaProjects = [];
        return;
      }
      try {
        this.asanaProjects = await fetchAsanaProjects(this.asanaConfig);
      } catch (err) {
        this.asanaProjectLoadError =
          (err && err.message) || "Failed to load Asana project directory";
        this.asanaProjects = [];
      }
    },
    async forceRemapAsanaProjectsFromApi() {
      this.loadingAsanaProjects = true;
      this.asanaProjectLoadError = "";
      try {
        this.asanaConfig.projectGids = "";
        this.persistAsanaQuiet();
        await this.refreshNow();
      } finally {
        this.loadingAsanaProjects = false;
      }
    },
    async refreshNow() {
      this.loading = true;
      this.loadError = "";
      this.sourceNotice = "";
      this.sourceErrors = {gitlab: "", asana: ""};
      /** @type {string[]} */
      const assistNotes = [];

      try {
        this.asanaConfig = saveAsanaConfig(this.asanaConfig);

        try {
          const primed = await ensureAsanaConnectionPrimed();
          this.asanaWorkspaceChoices = primed.workspaces;
          this.asanaConfig = saveAsanaConfig(primed.cfg);

          if (primed.didMapProjects) {
            assistNotes.push(
              "Mapped known NekoHR Asana projects automatically."
            );
          }
          if (
            String(this.asanaConfig.token || "").trim() &&
            !String(this.asanaConfig.workspaceGid || "").trim() &&
            this.asanaWorkspaceChoices.length > 1
          ) {
            assistNotes.push(
              "Choose your Asana workspace from the dropdown, then Refresh again."
            );
          }
        } catch (primErr) {
          assistNotes.push(
            `Asana setup assist skipped: ${
              (primErr && primErr.message) || String(primErr)
            }`
          );
        }

        const ctx = {
          username: this.userProfileData?.username || "",
          fullName:
            this.userProfileData?.full_name ||
            this.userProfileData?.username ||
            "",
        };
        const [gitlabRes, asanaRes] = await Promise.allSettled([
          fetchGitLabTasks(ctx),
          fetchAsanaTasks(),
        ]);
        const gitlabRows =
          gitlabRes.status === "fulfilled" ? gitlabRes.value : [];
        const asanaRows = asanaRes.status === "fulfilled" ? asanaRes.value : [];
        const rows = [...gitlabRows, ...asanaRows]
          .map((t) => normalizeTaskAssignee(t))
          .sort((a, b) =>
            String(b.updatedAt).localeCompare(String(a.updatedAt))
          );
        this.tasksBase = rows;

        if (gitlabRes.status === "rejected") {
          this.sourceErrors.gitlab =
            (gitlabRes.reason && gitlabRes.reason.message) ||
            "GitLab fetch failed";
        }
        if (asanaRes.status === "rejected") {
          this.sourceErrors.asana =
            (asanaRes.reason && asanaRes.reason.message) ||
            "Asana fetch failed";
        }

        /** @type {string[]} */
        const notices = [];
        if (this.sourceErrors.gitlab) {
          notices.push(`GitLab source error: ${this.sourceErrors.gitlab}`);
        }
        if (this.sourceErrors.asana) {
          notices.push(`Asana source error: ${this.sourceErrors.asana}`);
        }
        if (assistNotes.length) {
          notices.push(assistNotes.join(" "));
        }
        if (notices.length) {
          this.sourceNotice = notices.join(" ");
        }

        if (this.sourceCounts.gitlab === 0) {
          const hint = String(this.asanaConfig.gitlabToken || "").trim()
            ? "Verify GitLab PAT (Advanced → base URL if not nekogit)."
            : "Add a GitLab PAT under Connections.";
          this.sourceNotice =
            (this.sourceNotice ? `${this.sourceNotice} ` : "") +
            `No GitLab tasks returned. ${hint}`;
        }

        const hasWs = !!String(this.asanaConfig.workspaceGid || "").trim();
        const hasProj = !!String(this.asanaConfig.projectGids || "").trim();
        if (this.asanaConfig.token && !hasWs && !hasProj) {
          this.sourceNotice =
            (this.sourceNotice ? `${this.sourceNotice} ` : "") +
            "Asana needs a workspace (pick from the list when your account has several) before tasks can load.";
        }
        if (
          this.sourceCounts.asana === 0 &&
          this.asanaConfig.token &&
          (hasWs || hasProj)
        ) {
          this.sourceNotice = this.sourceNotice
            ? `${this.sourceNotice} Asana returned no tasks for this filter.`
            : "Asana returned no tasks for this filter.";
        }
      } catch (err) {
        this.loadError = `Failed to load Koneko B feed: ${
          (err && err.message) || "unknown error"
        }`;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
